import { type CompanyRiskBannerProps, type CompanyRiskBannerStatus } from '@context/companies'
import { formatDateHelper, isRiskProfileExpired } from '@repo/sdk/date'
import { formatCurrency } from '@repo/sdk/helpers'

type CompanyRiskBannerSource = {
  financials?: null | {
    credit_limit?: null | number
  }
  id: number
  payment_terms?: null | {
    title?: null | string
  }
  risk_profile?: null | {
    score?: null | string
    valid_to?: null | string
  }
  xero_attributes?: null | {
    outstanding_balance?: null | number
    overdue_outstanding_balance?: null | number
  }
}

const scoreStatusMap: Record<string, CompanyRiskBannerStatus> = {
  HIGH: 'high',
  LOW: 'low',
  MEDIUM: 'medium',
  'VERY HIGH': 'very-high',
  'VERY LOW': 'very-low',
}

const formatCurrencyLabel = (amount?: null | number) =>
  amount != null ? formatCurrency({ amount, showCurrencyCode: true }) : undefined

const getRiskStatus = (score?: null | string) => {
  const normalizedScore = score?.trim().toUpperCase()

  if (!normalizedScore) return 'not-verified'

  return scoreStatusMap[normalizedScore] ?? 'not-verified'
}

export const mapCompanyRiskBanner = (company: CompanyRiskBannerSource): CompanyRiskBannerProps => {
  const creditLimit = company.financials?.credit_limit
  const outstandingBalance = company.xero_attributes?.outstanding_balance ?? 0
  const overdueOutstandingBalance = company.xero_attributes?.overdue_outstanding_balance ?? 0
  const paymentTermsLabel = company.payment_terms?.title ?? '-'
  const detailsHref = `/management/customers/${company.id}/overview`

  if (!company.risk_profile) {
    return {
      detailsHref,
      paymentTermsLabel,
      status: 'unverified-new-company',
    }
  }

  if (isRiskProfileExpired(company.risk_profile.valid_to)) {
    return {
      detailsHref,
      paymentTermsLabel,
      status: 'not-verified',
    }
  }

  if (creditLimit != null && outstandingBalance > creditLimit) {
    const exceededAmount = outstandingBalance - creditLimit

    return {
      detailsHref,
      message: `Credit limit exceeded by ${formatCurrencyLabel(exceededAmount) ?? '-'}`,
      paymentTermsLabel,
      status: 'credit-limit-exceeded',
    }
  }

  if (overdueOutstandingBalance > 0) {
    return {
      detailsHref,
      message: `${formatCurrencyLabel(overdueOutstandingBalance) ?? '-'} in overdue payments`,
      paymentTermsLabel,
      status: 'overdue-payment',
    }
  }

  return {
    creditAvailableLabel: formatCurrencyLabel(
      creditLimit != null ? creditLimit - outstandingBalance : null,
    ),
    detailsHref,
    paymentTermsLabel,
    status: getRiskStatus(company.risk_profile.score),
    validUntilLabel: formatDateHelper({ date: company.risk_profile.valid_to }),
  }
}
